import React, { useRef, useState, useCallback, useEffect } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { Button, Modal, Form, Row, Col, Input, message, Select, Spin } from "antd";
import { connect } from "dva";
import { createNote, getCategoriesByType } from "@/services/notes";

const { TextArea } = Input;

const CreateNote = ({ configPerm, type, entityId, getNotes, onNotesCountChange }) => {
  const addNotesForm = useRef(null);
  const textAreaRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const cancelNewNote = useCallback(() => {
    setShowModal(false);
  }, []);

  useEffect(() => {
    if (showModal && type) {
      setCategoriesLoading(true);
      getCategoriesByType(type)
        .then(data => {
          setCategories(Array.isArray(data) ? data : []);
        })
        .finally(() => setCategoriesLoading(false));
    } else if (!showModal) {
      setCategories([]);
    }
  }, [showModal, type]);

  const addNote = useCallback(() => {
    addNotesForm.current.validateFields().then(data => {
      const hide = message.loading("Creating note...");
      setLoading(true);

      const { text, category } = data;

      createNote({ entityId, text, type, category })
        .then(() => {
          hide();
          message.success("Note created successfully!");

          addNotesForm.current.resetFields();

          if (typeof getNotes === "function") {
            getNotes().then(() => {
              setShowModal(false);
            });
          } else {
            setShowModal(false);
          }

          if (typeof onNotesCountChange === "function") {
            onNotesCountChange();
          }
        })
        .finally(() => {
          setLoading(false);
        });
    });
  }, [entityId, type, onNotesCountChange, getNotes]);

  useEffect(() => {
    let timeout;
    if (showModal) {
      timeout = setTimeout(() => {
        textAreaRef.current.focus();
      }, 300);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [showModal]);

  return (
    <>
      {configPerm["NoteService.Notes.Create"] && (
        <Button
          key="add-note"
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setShowModal(true);
          }}
          style={{
            borderRadius: 10,
            color: "#fff",
          }}
        />
      )}

      <Modal title="New Note" open={showModal} onOk={addNote} onCancel={cancelNewNote} confirmLoading={loading}>
        <Form layout="vertical" ref={addNotesForm} requiredMark={false}>
          <Row gutter={16}>
            <Col lg={24}>
              <Form.Item name="category" label="Category">
                {categoriesLoading ? (
                  <Spin size="small" />
                ) : (
                  <Select placeholder="Select category (optional)" allowClear showSearch optionFilterProp="children">
                    {categories.map(cat => (
                      <Select.Option key={cat.id || cat.value || cat} value={cat.id || cat.value || cat}>
                        {cat.name || cat.label || cat}
                      </Select.Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
            </Col>
            <Col lg={24}>
              <Form.Item name="text" label="Note" rules={[{ required: true }]}>
                <TextArea rows={4} ref={textAreaRef} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default connect(({ configs }) => ({
  configPerm: configs.configPerm,
}))(CreateNote);
